import { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from '../WebSocketContext';
import { useSnackbar } from '../../Utils/SnackBarContext';
import { useTranslation } from 'react-i18next';

// Custom hook for component-specific WebSocket handling
export const useComponentWebSocket = (commandHandlers = {}) => {
  const { ws, subscribeToMessages } = useWebSocket();
  
  // Setup component-specific WebSocket message handling
  useEffect(() => {
    if (!ws) return () => {};
    
    const handleMessage = (parsedMessage) => {
      const command = parsedMessage.command;
      if (command && commandHandlers[command]) {
        commandHandlers[command](parsedMessage);
      }
    };
    
    const unsubscribe = subscribeToMessages(handleMessage);
    return () => unsubscribe();
  }, [ws, subscribeToMessages, commandHandlers]);
  
  // Send WebSocket command with data
  const sendCommand = useCallback((command, data = {}) => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ command, data }));
    } else {
      console.error('WebSocket is not open');
    }
  }, [ws]);
  
  return { sendCommand, ws };
};

export const useGlobalSettingState = () => {
  const [isModified, setIsModified] = useState(false);
  const [credit, setCredit] = useState(0);
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const { ws, subscribeToMessages } = useWebSocket();

  // Shared state that might be used across multiple components
  const [configurationCompleted, setConfigurationCompleted] = useState(false);
  const [hasVisitedBefore, setHasVisitedBefore] = useState(false);

  useEffect(() => {
    // Check if the component has been visited before
    const visitedBefore = localStorage.getItem('globalSettingVisited');
    if (visitedBefore === 'true') {
      setHasVisitedBefore(true);
    } else {
      // Set flag that user has visited this page
      localStorage.setItem('globalSettingVisited', 'true');
    }

    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ command: 'configuration_check' }));
    }

    const handleMessage = (parsedMessage) => {
      switch (parsedMessage.command) {
        case 'configuration_check':
          setConfigurationCompleted(parsedMessage.data === '1');
          break;
        case 'configuration_data':
          setCredit(parseInt(parsedMessage.data.credits, 0) || 0);
          break;
        default:
          break;
      }
    };

    const unsubscribe = subscribeToMessages(handleMessage);

    return () => unsubscribe();
  }, [ws, subscribeToMessages]);

  const saveConfiguration = (configData) => {
    if (ws?.readyState === WebSocket.OPEN && isModified) {
      ws.send(
        JSON.stringify({
          command: 'update_configuration',
          data: configData
        })
      );
      console.log('Configuration data sent:', configData);
    } else {
      console.error('WebSocket is not open or no changes were made.');
    }
  };

  return {
    isModified,
    setIsModified,
    credit,
    setCredit,
    configurationCompleted,
    hasVisitedBefore,
    saveConfiguration,
    t,
    openSnackbar,
    ws,
    subscribeToMessages
  };
};

export const textModelOptions = [
  { value: 'none', label: 'Disabled', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'dataunfold', label: 'DataUnfold', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'openai', label: 'GPT 5 (OpenAI)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'groq', label: 'Llama 4 Maverick (Groq)', accuracy: '★★★★☆', accuracyColor: '#FF9800' },
  { value: 'azure', label: 'Azure OpenAI (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#757575' },
  { value: 'azure_claude', label: 'Azure Claude (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'azure_grok', label: 'Azure Grok (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'gemini', label: 'Gemini (Google Cloud)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'anthropic', label: 'Claude (Anthropic)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'deepseek', label: 'Deepseek R1 (DeepSeek Server)', accuracy: '★★★★☆', accuracyColor: '#FF9800' },
  { value: 'ollama', label: 'Deepseek R1 8B (Ollama)', accuracy: '★★☆☆☆', accuracyColor: '#F44336' },
  { value: 'ollama_qwen_vl', label: 'Qwen 2.5 VL (Ollama)', accuracy: '★★☆☆☆', accuracyColor: '#F44336' },
  { value: 'ollama_generic', label: 'Local Model (Ollama)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'aws_nova', label: 'Nova Pro (AWS)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'aws_claude', label: 'Claude (AWS)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'local', label: 'Local Model (Endpoint)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'dataunfold_mock', label: '*DataUnfold Dev Only*', accuracy: 'TEST', accuracyColor: '#9C27B0' },
  { value: 'mistral', label: 'Mistral', accuracy: '★★★☆☆', accuracyColor: '#FF9800' }
];

export const visionModelOptions = [
  { value: 'none', label: 'Disabled', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'dataunfold', label: 'DataUnfold', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'openai', label: 'GPT 5 (OpenAI)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'groq', label: 'Llama 4 Maverick(Groq)', accuracy: '★★★★☆', accuracyColor: '#FF9800' },
  { value: 'azure', label: 'Azure OpenAI (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#757575' },
  { value: 'azure_claude', label: 'Azure Claude (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'azure_grok', label: 'Azure Grok (AI Foundry)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'gemini', label: 'Gemini (Google Cloud)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'anthropic', label: 'Claude (Anthropic)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'ollama_qwen_vl', label: 'Qwen 2.5 VL (Ollama)', accuracy: '★★☆☆☆', accuracyColor: '#F44336' },
  { value: 'ollama_generic', label: 'Local Model (Ollama)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'aws_nova', label: 'Nova Pro (AWS)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'aws_claude', label: 'Claude (AWS)', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'local', label: 'Local Model (Endpoint)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'dataunfold_mock', label: '*DataUnfold Dev Only*', accuracy: 'TEST', accuracyColor: '#9C27B0' },
  { value: 'mistral', label: 'Mistral', accuracy: '★★★☆☆', accuracyColor: '#FF9800' }
];

export const audioModelOptions = [
  { value: 'none', label: 'Disabled', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'openai', label: 'OpenAI Whisper', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'dataunfold', label: 'DataUnfold Audio', accuracy: '★★★★★', accuracyColor: '#4CAF50' },
  { value: 'azure', label: 'Azure Speech Services', accuracy: '★★★★☆', accuracyColor: '#FF9800' },
  { value: 'groq', label: 'Groq Whisper', accuracy: '★★★★☆', accuracyColor: '#FF9800' },
  { value: 'ollama_generic', label: 'Local Model (Ollama)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'local', label: 'Local Model (Endpoint)', accuracy: 'N/A', accuracyColor: '#757575' },
  { value: 'dataunfold_mock', label: '*DataUnfold Dev Only*', accuracy: 'TEST', accuracyColor: '#9C27B0' }
];

// Models that do not support model editing
export const modelWithoutModelEditing = ['dataunfold_mock'];

export const resourceModels = ['azure', 'azure_claude', 'azure_grok', 'local'];
export const noTokenTextModels = ['dataunfold', 'ollama', 'ollama_qwen_vl', 'ollama_generic', 'none'];
export const noTokenVisionModels = ['dataunfold', 'ollama_qwen_vl', 'ollama_generic', 'none'];
export const noTokenAudioModels = ['dataunfold', 'ollama_generic', 'none'];
export const displayModelName = ['ollama_generic'];

// Providers that support advanced tuning (max_tokens, temperature)
export const tunableProviders = new Set([
  'openai',
  'anthropic',
  'azure_claude',
  'azure_grok',
  'groq',
  'mistral',
  'deepseek',
  'aws_claude',
  'aws_nova',
  'gemini'
]);

// Shared provider icons and small descriptions used in dropdowns
export const providerIcons = {
  'none': { icon: `/images/ai/none.png`, description: 'Disabled', color: '#757575' },
  'openai': { icon: `/images/ai/openai.png`, description: 'OpenAI', color: '#00A67E' },
  'groq': { icon: `/images/ai/groq.png`, description: 'Groq', color: '#F97316' },
  'azure': { icon: `/images/ai/azure.png`, description: 'Azure OpenAI', color: '#0078D4' },
  'azure_claude': { icon: `/images/ai/azure.png`, description: 'Azure Claude', color: '#0078D4' },
  'azure_grok': { icon: `/images/ai/azure.png`, description: 'Azure Grok', color: '#0078D4' },
  'gemini': { icon: `/images/ai/gemini.png`, description: 'Gemini (Google Cloud)', color: '#4285F4' },
  'anthropic': { icon: `/images/ai/claude.png`, description: 'Anthropic (Claude)', color: '#D2691E' },
  'deepseek': { icon: `/images/ai/deepseek.png`, description: 'DeepSeek', color: '#6366F1' },
  'dataunfold': { icon: `/images/ai/dataunfold.png`, description: 'DataUnfold', color: '#5757b0' },
  'ollama': { icon: `/images/ai/ollama.png`, description: 'Ollama', color: '#10B981' },
  'ollama_qwen_vl': { icon: `/images/ai/ollama.png`, description: 'Qwen VL (Ollama)', color: '#10B981' },
  'ollama_generic': { icon: `/images/ai/ollama.png`, description: 'Local Ollama', color: '#10B981' },
  'aws_nova': { icon: `/images/ai/nova.png`, description: 'AWS Nova', color: '#FF9900' },
  'aws_claude': { icon: `/images/ai/claude.png`, description: 'AWS Claude', color: '#232F3E' },
  'local': { icon: `/images/ai/local.png`, description: 'Local Model', color: '#666666' },
  'mistral': { icon: `/images/ai/mistral.png`, description: 'Mistral', color: '#FF6F61' }
};
